// A projeção das ofertas de carro da conversa no vocabulário do
// intelligence-ui-kit — a mesma para os "Melhores carros" do chat e para a
// tela em diálogo "Ver todos os carros". O kit desenha; aqui só se traduz e se
// escreve o porquê de cada oferta da seleção curta, a partir dos FATOS do
// agente (`highlight`), como o ônibus (`bus_results_copy.ts`).
import {
  toRentalFilterableOffers,
  type RentalOfferView,
  type RentalResultsSource,
  type RentalResultsViewCopy,
} from '@voll-intelligence/intelligence-ui-kit';
import { fmtMoneyWhole } from '../../../shared/ui/flight_format.js';
import type { GenUiLocale, GenUiTranslate } from '../../../shared/ui/i18n.js';
import type { ChatUiRentalOffer, ChatUiRentalSearchMeta } from '@voll-intelligence/genui-contract';

export function rentalResultsViewCopy(t: GenUiTranslate): RentalResultsViewCopy {
  return {
    orSimilar: t('rental.vehicle.orSimilar'),
    priceDetail: (days, daily) =>
      t(days === 1 ? 'rental.results.priceDetail_one' : 'rental.results.priceDetail_other', {
        count: days,
        daily: fmtMoneyWhole(daily),
      }),
    dailyPrice: (daily) => t('rental.results.dailyPrice', { daily: fmtMoneyWhole(daily) }),
    outOfPolicy: t('rental.outPolicy'),
    cheapestBadge: t('rental.results.cheapest'),
    agencyFallback: (company) => t('rental.results.agencyFallback', { company }),
    // Sem centavos, como o card do hotel (#1983) e o do voo.
    priceLabel: fmtMoneyWhole,
  };
}

/** Os pontos da busca como a conversa os conhece: o bloco não ecoa o lugar
 *  pedido, então a agência da primeira oferta que a nomeia é o melhor rótulo —
 *  pelo nome tratado do BFF quando a oferta o traz (#5125). Vazio quando
 *  nenhuma nomeia — a página cai no título genérico. Sem o tipo do lugar, o
 *  card mostra o nome da agência, nunca este rótulo. */
export function rentalStopsOf(
  offers: readonly ChatUiRentalOffer[],
): RentalResultsSource['pickup'][] {
  // O rótulo pode vir só do endereço, sem nome cru: também nomeia a ponta.
  const named = offers.find((offer) => offer.pickUpAgencyLabel ?? offer.pickUpAgency);
  const pickup = named?.pickUpAgencyLabel ?? named?.pickUpAgency ?? '';
  const namedDropoff = offers.find((offer) => offer.dropOffAgencyLabel ?? offer.dropOffAgency);
  const dropoff = namedDropoff?.dropOffAgencyLabel ?? namedDropoff?.dropOffAgency ?? pickup;
  return [{ label: pickup }, { label: dropoff }];
}

type RentalHighlight = NonNullable<ChatUiRentalOffer['highlight']>;
type OfferBadgeView = RentalOfferView['badges'][number];

const HIGHLIGHT_ICON = {
  recommended: 'recommended',
  cheapest: 'cheapest',
  closest: 'closest',
} as const;

/** "450 m" abaixo de 1 km (de 10 em 10), "1,2 km" até 10 e "14 km" acima — no
 *  número do locale da conversa. */
export function distanceLabel(km: number, locale: GenUiLocale): string {
  if (km < 1) return `${String(Math.max(10, Math.round(km * 100) * 10))} m`;
  const digits = km < 10 ? 1 : 0;
  const value = new Intl.NumberFormat(locale, { maximumFractionDigits: digits }).format(km);
  return `${value} km`;
}

/** A nota de mérito no pé do card, a partir dos FATOS do agente. O recomendado
 *  fora da política (nenhuma oferta dentro) não diz "dentro da política". */
function meritNoteOf(
  t: GenUiTranslate,
  locale: GenUiLocale,
  offer: ChatUiRentalOffer,
  highlight: RentalHighlight,
): string {
  if (highlight.kind === 'recommended') {
    return offer.withinPolicy
      ? t('rentalOffers.merit.recommended')
      : t('rentalOffers.merit.recommendedOutside');
  }
  if (highlight.kind === 'cheapest') {
    const amount = fmtMoneyWhole({ amount: highlight.cheaperBy, currency: offer.price.currency });
    return t('rentalOffers.merit.cheapest', { amount });
  }
  return t('rentalOffers.merit.closest', { distance: distanceLabel(highlight.distanceKm, locale) });
}

function highlightBadge(t: GenUiTranslate, highlight: RentalHighlight): OfferBadgeView {
  return {
    id: `highlight-${highlight.kind}`,
    label: t(`rentalOffers.badge.${highlight.kind}`),
    // O recomendado é o selo sólido (a escolha do agente); os outros, fatos.
    tone: highlight.kind === 'recommended' ? 'within' : 'unknown',
    icon: HIGHLIGHT_ICON[highlight.kind],
  };
}

/**
 * As views dos cards da seleção curta, na ordem do agente: o selo do eixo e a
 * nota do porquê vêm do `highlight`, e só dele — o selo "menor preço da página"
 * que a projeção do kit calcula sozinha não vale aqui (o eixo é do agente).
 * Sem `highlight` (os blocos antigos, ou o terceiro card sem eixo): sem selo.
 */
export function rentalShortlistViews(
  i18n: { t: GenUiTranslate; locale: GenUiLocale },
  offers: readonly ChatUiRentalOffer[],
  metadata: ChatUiRentalSearchMeta,
  selectable: boolean,
): RentalOfferView[] {
  const { t, locale } = i18n;
  const [pickup, dropoff] = rentalStopsOf(offers);
  const source: RentalResultsSource = {
    facets: metadata,
    pickup: pickup ?? { label: '' },
    dropoff: dropoff ?? { label: '' },
  };
  const projected = toRentalFilterableOffers(offers, source, rentalResultsViewCopy(t), selectable);
  return projected.map(({ offer: view }, index) => {
    const offer = offers[index];
    const highlight = offer?.highlight;
    if (!offer || !highlight) return { ...view, badges: [] };
    return {
      ...view,
      badges: [highlightBadge(t, highlight)],
      meritNote: meritNoteOf(t, locale, offer, highlight),
    };
  });
}
